import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * AdminTenantQR — discovery hub for the Tenant QR Portal.
 *
 * Lists every property (+ its units) with:
 *   - "Preview" opens the public tenant portal for that unit in a new tab
 *   - "Print QR" opens /admin/qr-poster?slug={token} for print
 *   - "Rotate" mints a fresh token (old link stops working immediately —
 *     useful on turnover so the previous tenant loses access).
 *
 * All heavy lifting already lives in the backend; this page just makes
 * the feature discoverable instead of buried under /admin/qr-poster.
 */
export default function AdminTenantQR() {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [tokens, setTokens] = useState({});   // {addr::unit → token}

  useEffect(() => {
    api.get("/properties/list")
      .then(r => setProps(r.data?.properties || []))
      .catch(() => toast.error("Couldn't load properties"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const list = Array.isArray(props) ? props : [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(p => (p.address || "").toLowerCase().includes(needle));
  }, [props, q]);

  // One row per property. Per-unit stickers can be added later from the
  // property detail page — the backend already scopes tokens per (address, unit).
  const rows = useMemo(() => {
    return (filtered || []).map(p => ({
      address: p.address,
      address_key: p.normalized_address || p.address,
      unit: "",
      property_name: p.property_name || "",
      open_jobs: p.open_jobs || 0,
    }));
  }, [filtered]);

  const rowKey = (r) => `${r.address_key}::${r.unit || ""}`;

  const ensureToken = async (row, opts = { rotate: false }) => {
    const key = rowKey(row);
    setBusyKey(key);
    try {
      const endpoint = opts.rotate
        ? "/properties/request-radar-token/regenerate"
        : "/properties/request-radar-token";
      const params = { address: row.address, unit: row.unit || "" };
      const r = opts.rotate
        ? await api.post(endpoint, null, { params })
        : await api.get(endpoint, { params });
      setTokens(t => ({ ...t, [key]: r.data.token }));
      if (opts.rotate) toast.success("Old QR retired — new link is live");
      return r.data.token;
    } catch (e) {
      toast.error("Couldn't generate QR — try again in a sec");
      return null;
    } finally {
      setBusyKey(null);
    }
  };

  const preview = async (row) => {
    const token = tokens[rowKey(row)] || await ensureToken(row);
    if (token) window.open(`/report/${token}`, "_blank", "noopener");
  };

  const print = async (row) => {
    const token = tokens[rowKey(row)] || await ensureToken(row);
    if (!token) return;
    const title = encodeURIComponent(row.property_name || row.address);
    const address = encodeURIComponent(`${row.address}${row.unit ? " · Unit " + row.unit : ""}`);
    window.open(`/admin/qr-poster?slug=${token}&title=${title}&address=${address}`, "_blank", "noopener");
  };

  const rotate = async (row) => {
    if (!window.confirm(
      `Rotate the QR for ${row.address}${row.unit ? " · " + row.unit : ""}?\n\n` +
      `The old sticker will stop working immediately. Only do this after a turnover so the previous tenant loses access.`
    )) return;
    await ensureToken(row, { rotate: true });
  };

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-5">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-2xl flex-shrink-0">📡</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-black text-[#0F1F3A]">Tenant QR Codes</h1>
                <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-500/15 text-emerald-700 border border-emerald-500/40 px-2 py-0.5 rounded-full">Live</span>
              </div>
              <p className="text-sm text-[#8B93A7] font-medium mt-1">
                One printable QR per unit. Tenants scan → auto-detected unit → describe issue → photo → done. No app, no login.
                Every submission lands in <Link to="/admin/radars" className="text-cyan-700 font-black hover:underline">Request Radar</Link>.
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-3">
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by address or property name…"
            data-testid="tenant-qr-search"
            className="w-full h-11 px-4 border-2 border-slate-200 rounded-lg text-sm font-medium focus:border-cyan-500 focus:outline-none"/>
        </div>

        {/* Rows */}
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500 font-medium">Loading properties…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center">
              <div className="text-4xl mb-2">🏠</div>
              <div className="text-sm font-black text-[#0F1F3A]">No properties yet</div>
              <div className="text-xs text-slate-500 font-medium mt-1">Add a property first, then come back here to print its QR sticker.</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map(row => {
                const key = rowKey(row);
                const token = tokens[key];
                const busy = busyKey === key;
                return (
                  <div key={key}
                    data-testid={`tenant-qr-row-${key}`}
                    className="flex items-center gap-3 p-3 sm:p-4 hover:bg-slate-50 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-black text-[#0F1F3A] truncate">{row.address || "Unknown address"}</div>
                        {row.unit && (
                          <span className="text-[10px] font-black uppercase tracking-widest bg-amber-500/15 text-amber-700 border border-amber-500/40 px-1.5 py-0.5 rounded">
                            Unit {row.unit}
                          </span>
                        )}
                        {row.open_jobs > 0 && (
                          <span className="text-[10px] font-black uppercase tracking-widest bg-cyan-500/15 text-cyan-700 border border-cyan-500/40 px-1.5 py-0.5 rounded">
                            {row.open_jobs} open
                          </span>
                        )}
                      </div>
                      {row.property_name && row.property_name !== row.address && (
                        <div className="text-[11px] text-slate-500 font-medium truncate mt-0.5">{row.property_name}</div>
                      )}
                      {token && (
                        <div className="text-[9px] font-mono text-slate-400 truncate mt-1">/report/{token.slice(0, 12)}…</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => preview(row)}
                        disabled={busy}
                        data-testid={`tenant-qr-preview-${key}`}
                        className="h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 font-black uppercase text-[10px] tracking-widest">
                        Preview
                      </button>
                      <button onClick={() => print(row)}
                        disabled={busy}
                        data-testid={`tenant-qr-print-${key}`}
                        className="h-9 px-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-slate-900 font-black uppercase text-[10px] tracking-widest shadow-[0_0_16px_-4px_#22D3EE]">
                        {busy ? "…" : "🖨 Print QR"}
                      </button>
                      <button onClick={() => rotate(row)}
                        disabled={busy}
                        data-testid={`tenant-qr-rotate-${key}`}
                        title="Rotate token — old sticker stops working immediately"
                        className="h-9 px-2 rounded-lg bg-transparent hover:bg-red-50 disabled:opacity-50 text-red-600 font-black uppercase text-[10px] tracking-widest">
                        ↻
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
